import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Marker, TileLayer, Tooltip, useMap } from "react-leaflet";
import L, { type LatLngExpression } from "leaflet";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import {
  listNearbyRitualSessions,
  subscribeNearbyRitualSessions,
  type RitualSessionSummary,
} from "@/lib/supabase/rituals";
import { useToast } from "@/hooks/use-toast";

import { LocateFixed, Lock, MapPin, Navigation, SlidersHorizontal, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl, resilientFetch } from "@/lib/supabase/client";

const MAP_CENTER = { lat: 41.04, lng: 29.0 };

type PreciseLocation = {
  lat: number;
  lng: number;
  accuracy: number;
};

type RitualActivityItem = {
  id: string;
  title: string;
  active: boolean;
  tags: string[];
};

function distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthR = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return earthR * c;
}

async function getPreciseLocationSample() {
  if (!navigator.geolocation) throw new Error("Bu cihazda konum servisi yok.");

  return await new Promise<PreciseLocation>((resolve, reject) => {
    const startedAt = Date.now();
    let best: PreciseLocation | null = null;

    const done = (value: PreciseLocation | null) => {
      if (!value) {
        reject(new Error("Konum alınamadı."));
        return;
      }
      resolve(value);
    };

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const next = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Number.isFinite(pos.coords.accuracy) ? Math.max(0, pos.coords.accuracy) : 9999,
        };

        if (!best || next.accuracy < best.accuracy) best = next;

        if (next.accuracy <= 80 || Date.now() - startedAt > 12000) {
          navigator.geolocation.clearWatch(watchId);
          done(best);
        }
      },
      () => {
        navigator.geolocation.clearWatch(watchId);
        done(best);
      },
      { enableHighAccuracy: true, timeout: 14000, maximumAge: 0 },
    );

    setTimeout(() => {
      navigator.geolocation.clearWatch(watchId);
      done(best);
    }, 15000);
  });
}

function normalizeRitualTitle(value: string) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ");
}

function parseRitualScheduleFromTags(tags: string[]) {
  const rawTag = (tags ?? []).find((tag) => String(tag ?? "").startsWith("rituel_saat:")) ?? "";
  const value = String(rawTag).slice("rituel_saat:".length).trim();
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value) ? value : "";
}

function activityEmoji(activity: string | null | undefined) {
  const value = String(activity ?? "").trim().toLocaleLowerCase("tr-TR");
  if (value.includes("koş") || value.includes("kos") || value.includes("run")) return "🏃";
  if (value.includes("medit")) return "🧘";
  if (value.includes("yoga")) return "🧘";
  if (value.includes("yürü") || value.includes("yuru") || value.includes("walk")) return "🚶";
  if (value.includes("dans")) return "💃";
  return "✨";
}

function ritualPinIcon(activity: string | null | undefined) {
  const emoji = activityEmoji(activity);
  return L.divIcon({
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    html: `<span style="display:grid;place-items:center;width:34px;height:34px;border-radius:9999px;background:#7c3aed;border:3px solid #fff;box-shadow:0 8px 18px rgba(76,29,149,.35);font-size:16px;line-height:1;">${emoji}</span>`,
  });
}

const userPinIcon = L.divIcon({
  className: "",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: '<span style="display:grid;place-items:center;width:26px;height:26px;border-radius:9999px;background:#0ea5e9;border:3px solid #fff;box-shadow:0 8px 18px rgba(2,132,199,.35);"><span style="width:6px;height:6px;border-radius:9999px;background:#fff;"></span></span>',
});

function FocusUserLocationOnRefresh({
  location,
  revision,
}: {
  location: { lat: number; lng: number } | null;
  revision: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (!location || revision <= 0) return;
    map.flyTo([location.lat, location.lng], Math.max(map.getZoom(), 14), { duration: 0.7 });
  }, [location, map, revision]);

  return null;
}

function readAccessTokenFromStorage() {
  try {
    const parseToken = (raw: string) => {
      if (!raw) return "";
      const direct = raw.trim();
      if (direct && !direct.startsWith("{")) return direct;

      const parsed = JSON.parse(raw) as any;
      const fromRoot = typeof parsed?.access_token === "string" ? parsed.access_token : "";
      if (fromRoot) return fromRoot;
      const fromCurrentSession = typeof parsed?.currentSession?.access_token === "string" ? parsed.currentSession.access_token : "";
      if (fromCurrentSession) return fromCurrentSession;
      const fromSession = typeof parsed?.session?.access_token === "string" ? parsed.session.access_token : "";
      return fromSession || "";
    };

    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
    const fromSupabaseStorage = storageKey ? parseToken(globalThis.localStorage?.getItem(storageKey) ?? "") : "";
    if (fromSupabaseStorage) return fromSupabaseStorage;

    const fromLegacyKey = parseToken(globalThis.localStorage?.getItem("supabase.auth.token") ?? "");
    if (fromLegacyKey) return fromLegacyKey;

    return "";
  } catch {
    return "";
  }
}

function mapBoundsForQuery(location: { lat: number; lng: number } | null) {
  const center = location ?? MAP_CENTER;
  const latDelta = 0.25;
  const lngDelta = 0.35;

  return {
    minLat: center.lat - latDelta,
    maxLat: center.lat + latDelta,
    minLng: center.lng - lngDelta,
    maxLng: center.lng + lngDelta,
  };
}

async function fetchMembershipSummary() {
  const token = readAccessTokenFromStorage();
  if (!token) return null;

  const res = await resilientFetch("/api/numeroloji/me/entitlements", {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) return null;
  const payload = (await res.json().catch(() => null)) as any;
  return payload?.membership ?? null;
}

async function fetchRitualActivities() {
  const res = await resilientFetch("/api/numeroloji/activities");
  const payload = (await res.json().catch(() => null)) as any;
  if (!res.ok) return [] as RitualActivityItem[];
  const items = Array.isArray(payload?.items) ? payload.items : [];

  return items
    .map((item: any) => ({
      id: String(item?.id ?? "").trim(),
      title: String(item?.title ?? "").trim(),
      active: Boolean(item?.active),
      tags: Array.isArray(item?.tags) ? item.tags.map((tag: unknown) => String(tag ?? "").trim()).filter(Boolean) : [],
    }))
    .filter((item: RitualActivityItem) => item.id && item.title) as RitualActivityItem[];
}

export default function UserPanelYakinimdakiRitueller() {
  const { user } = useSupabaseSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<RitualSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [userLocationRevision, setUserLocationRevision] = useState(0);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
  const [locating, setLocating] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<"all" | "near" | "meditation" | "now">("all");

  const lastLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastAccuracyRef = useRef<number | null>(null);

  useEffect(() => {
    lastLocationRef.current = userLocation;
  }, [userLocation]);

  useEffect(() => {
    lastAccuracyRef.current = locationAccuracy;
  }, [locationAccuracy]);

  const refreshUserLocation = useCallback(async () => {
    setLocating(true);
    try {
      const precise = await getPreciseLocationSample();

      const nextPoint = { lat: precise.lat, lng: precise.lng };
      const prevPoint = lastLocationRef.current;

      if (precise.accuracy > 180) {
        toast({
          title: "Düşük konum doğruluğu",
          description: "GPS doğruluğu yetersiz. Açık alanda tekrar dene.",
          variant: "destructive",
        });
        return;
      }

      if (!prevPoint) {
        setUserLocation(nextPoint);
        setLocationAccuracy(precise.accuracy);
        setUserLocationRevision((v) => v + 1);
        return;
      }

      const movedMeters = distanceMeters(prevPoint, nextPoint);
      const prevAccuracy = lastAccuracyRef.current ?? 9999;

      const closeEnough = movedMeters <= 350;
      const acceptableNearbyUpdate = movedMeters <= 1000 && precise.accuracy <= 80;
      const trustedFarUpdate = movedMeters > 1000 && precise.accuracy <= 60 && prevAccuracy > 120;

      if (closeEnough || acceptableNearbyUpdate || trustedFarUpdate) {
        setUserLocation(nextPoint);
        setLocationAccuracy(precise.accuracy);
        setUserLocationRevision((v) => v + 1);
      } else {
        toast({
          title: "Düşük konum doğruluğu",
          description: "Yeni konum doğrulanamadı, mevcut konum korundu.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Konum alınamadı",
        description: "Konum iznini açıp tekrar dene.",
        variant: "destructive",
      });
    } finally {
      setLocating(false);
    }
  }, [toast]);

  useEffect(() => {
    void refreshUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadSessions = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const bounds = mapBoundsForQuery(userLocation);
        const rows = await listNearbyRitualSessions({
          minLat: bounds.minLat,
          maxLat: bounds.maxLat,
          minLng: bounds.minLng,
          maxLng: bounds.maxLng,
          limit: 120,
        });

        if (cancelled) return;
        setSessions(rows);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Ritüeller alınamadı.";
        if (!silent) toast({ title: "Yükleme hatası", description: msg });
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };

    void loadSessions();

    const unsubscribe = subscribeNearbyRitualSessions(() => {
      void loadSessions(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [toast, user?.id, userLocation]);

  useEffect(() => {
    if (!selectedSessionId) return;
    if (!sessions.some((session) => session.id === selectedSessionId)) {
      setSelectedSessionId(null);
    }
  }, [selectedSessionId, sessions]);

  const entitlementsQuery = useQuery({
    queryKey: ["numeroloji-membership-summary", user?.id],
    queryFn: fetchMembershipSummary,
    enabled: Boolean(user?.id),
    staleTime: 30_000,
    retry: false,
  });

  const ritualActivitiesQuery = useQuery({
    queryKey: ["numeroloji-ritual-activities-for-schedule"],
    queryFn: fetchRitualActivities,
    staleTime: 60_000,
    retry: false,
  });

  const hasResolvedMembershipSnapshot = Boolean(entitlementsQuery.data && typeof entitlementsQuery.data === "object");

  const hasNearbyRitualsAccess = useMemo(() => {
    const raw = entitlementsQuery.data?.hasFeature;
    if (!raw || typeof raw !== "object") return false;
    return Boolean((raw as Record<string, unknown>).nearby_rituals);
  }, [entitlementsQuery.data?.hasFeature]);

  const shouldShowUpgradeCard = hasResolvedMembershipSnapshot && !hasNearbyRitualsAccess;

  const scheduleByTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of ritualActivitiesQuery.data ?? []) {
      if (!item.active) continue;
      const schedule = parseRitualScheduleFromTags(item.tags ?? []);
      if (!schedule) continue;
      map.set(normalizeRitualTitle(item.title), schedule);
    }
    return map;
  }, [ritualActivitiesQuery.data]);

  const selectedSession = sessions.find((session) => session.id === selectedSessionId) ?? null;

  const filteredSessions = useMemo(() => {
    if (activeFilter === "all") return sessions;
    if (activeFilter === "meditation") {
      return sessions.filter((session) => String(session.circleActivity ?? "").toLocaleLowerCase("tr-TR").includes("medit"));
    }
    if (activeFilter === "near") {
      if (!userLocation) return sessions;
      return sessions.filter((session) => distanceMeters(userLocation, { lat: session.lat, lng: session.lng }) <= 2000);
    }
    if (activeFilter === "now") {
      return sessions.filter((session) => session.memberCount > 0);
    }
    return sessions;
  }, [activeFilter, sessions, userLocation]);

  const featuredSessions = filteredSessions.slice(0, 3);

  const getDistanceLabel = (session: RitualSessionSummary) => {
    if (!userLocation) return "Yakında";
    const km = distanceMeters(userLocation, { lat: session.lat, lng: session.lng }) / 1000;
    if (!Number.isFinite(km)) return "Yakında";
    return `${km.toFixed(km < 10 ? 1 : 0)} km`;
  };

  const onOpenSessionChat = (session: RitualSessionSummary) => {
    navigate(`/panel/cember/${session.circleId}?tab=sohbet`);
  };

  return (
    <div className="mx-auto w-full max-w-[760px] pb-[calc(env(safe-area-inset-bottom)+8rem)]">
      <div className="relative isolate overflow-hidden rounded-[2.25rem] border border-[#e7dcec] bg-[#fbf4ff] shadow-[0_20px_40px_rgba(112,42,225,0.14)]">
        <div className="relative z-20 px-5 pb-3 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900">Yakınımdaki Ritüeller</h1>
              <p className="mt-1.5 text-sm text-slate-600">Pine tıklayıp ritüel kartını görüntüle, sonra sohbete katıl.</p>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
              <Navigation className="h-4 w-4 text-slate-500" />
              {userLocation
                ? locationAccuracy && locationAccuracy > 300
                  ? `Yaklaşık konum (${Math.round(locationAccuracy)}m)`
                  : "Konum alındı"
                : "Konum bekleniyor"}
            </div>
          </div>
        </div>

        <div className="relative z-0 h-[300px] overflow-hidden bg-gradient-to-br from-[#5f4c82] via-[#74609a] to-[#8f76b6]">
          <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_45%),radial-gradient(circle_at_85%_70%,rgba(255,255,255,0.12),transparent_40%)]" />

          <MapContainer
            center={MAP_CENTER as LatLngExpression}
            zoom={14}
            minZoom={2}
            maxZoom={19}
            className="relative z-0 h-full w-full"
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />

            {filteredSessions.map((session) => (
              <Marker
                key={session.id}
                position={[session.lat, session.lng]}
                icon={ritualPinIcon(session.circleActivity)}
                eventHandlers={{
                  click: () => {
                    setSelectedSessionId(session.id);
                  },
                }}
              >
                <Tooltip direction="top" offset={[0, -24]}>
                  {session.title}
                </Tooltip>
              </Marker>
            ))}

            {userLocation ? (
              <>
                <CircleMarker
                  center={[userLocation.lat, userLocation.lng]}
                  radius={18}
                  pathOptions={{ color: "#38bdf8", fillColor: "#38bdf8", fillOpacity: 0.18, weight: 2 }}
                />
                <Marker position={[userLocation.lat, userLocation.lng]} icon={userPinIcon}>
                  <Tooltip direction="top" offset={[0, -12]}>
                    Senin konumun
                  </Tooltip>
                </Marker>
              </>
            ) : null}

            <FocusUserLocationOnRefresh location={userLocation} revision={userLocationRevision} />
          </MapContainer>

          <button
            type="button"
            className="absolute right-4 top-4 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--panel-primary)] text-white shadow-xl shadow-[color:rgba(55,19,236,0.35)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
            onClick={refreshUserLocation}
            disabled={locating}
            aria-label={locating ? "Konum güncelleniyor" : "Konumu düzelt"}
            title={locating ? "Konum güncelleniyor" : "Konumu düzelt"}
          >
            <LocateFixed className="h-4 w-4" />
          </button>

        </div>

        <div className="relative z-20 -mt-9 rounded-t-[3.05rem] bg-[#fbf4ff] px-5 pb-6 pt-7 shadow-[0_-14px_28px_rgba(112,42,225,0.10)]">
          <div className="relative z-30 -mt-2 mb-4 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
            {[
              { key: "all", label: "Tümü" },
              { key: "near", label: "Yakın (2km)" },
              { key: "meditation", label: "Meditasyon" },
              { key: "now", label: "Şimdi" },
            ].map((chip) => {
              const active = chip.key === activeFilter;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setActiveFilter(chip.key as "all" | "near" | "meditation" | "now")}
                  className={active
                    ? "shrink-0 rounded-full bg-[var(--panel-primary)] px-4 py-2 text-xs font-bold text-white shadow-[0_8px_18px_rgba(112,42,225,0.38)]"
                    : "shrink-0 rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_6px_14px_rgba(15,23,42,0.12)]"}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-3xl font-black tracking-tight text-[#12172b]">Yakındaki Ritüeller</h2>
              <p className="mt-2 text-sm text-slate-600">Ruhun için doğru anı keşfet</p>
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-[#f1e6f5] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#7a4a97]">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Pro
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {featuredSessions.length ? (
              featuredSessions.map((session, index) => {
                const selected = selectedSession?.id === session.id;
                const titleKey = normalizeRitualTitle(session.title);
                const adminDefinedSchedule = scheduleByTitle.get(titleKey) ?? "";
                const fallbackTime = ["20:30", "07:15", "19:40"][index] ?? "21:00";
                const timeLabel = adminDefinedSchedule || fallbackTime;
                return (
                  <Card
                    key={session.id}
                    className={selected
                      ? "rounded-2xl border border-[#ece1f0] bg-white px-4 py-4 shadow-[0_10px_30px_rgba(112,42,225,0.06)]"
                      : "rounded-2xl border border-[#ece1f0] bg-white px-4 py-4 shadow-[0_10px_30px_rgba(112,42,225,0.06)]"}
                    onClick={() => setSelectedSessionId(session.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="mt-0.5 grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#f1e6f5] text-[var(--panel-primary)]">
                          {activityEmoji(session.circleActivity)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-lg font-bold text-[#12172b]">{session.title}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                            <MapPin className="h-3.5 w-3.5" />
                            <span className="truncate">{session.circleName}</span>
                            <span>•</span>
                            <span className="shrink-0">{getDistanceLabel(session)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 rounded-xl bg-[#f3ecf8] px-3 py-1 text-sm font-bold text-[var(--panel-primary)]">{timeLabel}</div>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        <Users className="h-3.5 w-3.5" />
                        {session.memberCount} kişi burada
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 rounded-full bg-[var(--panel-primary)] px-5 text-sm font-semibold text-white hover:brightness-110"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenSessionChat(session);
                        }}
                      >
                        Katıl
                      </Button>
                    </div>
                  </Card>
                );
              })
            ) : (
              <Card className="rounded-3xl border border-dashed border-slate-300 bg-white/70 p-4 text-sm text-slate-600">
                Bu filtre için ritüel bulunamadı.
              </Card>
            )}
          </div>

          {shouldShowUpgradeCard ? (
            <div className="relative mt-4 overflow-hidden rounded-3xl border border-[#eadcf2] bg-white/85 p-5">
              <div className="pointer-events-none absolute inset-0 bg-white/72 backdrop-blur-[2px]" />
              <div className="relative">
                <div className="text-2xl font-black tracking-tight text-[#171d30] sm:text-3xl">Yakınındaki Enerjileri Gör</div>
                <p className="mt-1 text-base text-slate-700 sm:text-lg">Pro üyeliğe geçerek çevrendeki ritüellere katıl ve topluluğun bir parçası ol.</p>
                <div className="mt-4">
                  <Button type="button" className="h-12 rounded-full bg-[var(--panel-primary)] px-7 text-base font-bold text-white hover:brightness-110">
                    <Lock className="mr-2 h-4 w-4" />
                    Hemen Yükselt
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
